
import { createHash } from 'crypto'
import { BModal } from 'bootstrap-vue'
import { Component, Prop, Ref, Vue } from 'nuxt-property-decorator'
import { v4 as uuidv4 } from 'uuid'
import { waitUntil } from 'async-wait-until'
import NoSleep from 'nosleep.js'
import { EngineService, PlaybackStatus } from './utils/EngineService'
import { UtteranceButton } from './model/UtteranceButton'
import { Display } from './model/Display'
import { WebPlayerThemeUtil } from './utils/WebPlayerThemeUtil'
import MicListener from './MicListener.vue'
import { GameEvent } from './model/GameEvent'
import { AudioQuality } from './model/AudioQuality'
import WebPlayerControls from './WebPlayerControls.vue'
import { GPSLocation } from './model/GeolocationNode'
import {
  GeolocationService,
  GeolocationServiceStatus,
} from './utils/geolocationService/GeolocationService'
import { GPSLocationExtension } from './utils/geolocationService/GPSLocationExtension'
import { Position } from './utils/geolocationService/Position'
import {
  AislocationService,
  AislocationServiceStatus,
} from './utils/aislocationService/AislocationService'
import { EngineResponse } from './model/EngineResponse'
import { Layout, WebPlayer } from '~/api'

@Component({})
export default class WebPlayerComponent extends Vue {
  @Ref('info-modal')
  infoModal!: BModal

  @Ref('mic-listener')
  micListener?: MicListener

  @Ref('web-player-controls')
  webPlayerControls!: WebPlayerControls

  @Prop() versionId!: string
  @Prop() projectId!: string
  @Prop() platformSettings!: WebPlayer
  @Prop() embed?: boolean
  @Prop({ default: true }) micSupported!: boolean
  @Prop({ default: undefined }) mmsi?: string | undefined

  engineService: EngineService | null = null
  audioQuality = AudioQuality.MEDIUM
  playFromVideoFrame = false

  // UI State
  utteranceButtons: UtteranceButton[] = []
  audioUrl: string | null = null
  display: Display | null = null
  youtube: GameEvent | null = null
  video: GameEvent | null = null
  manualUserInput: string = ''
  videoEnded = false

  // Geolocation
  geolocationService: GeolocationService | null = null
  aislocationService: AislocationService | null = null
  firstLocation: Position | null = null
  lastTriggeredPositionHash: string | null = null
  gpsLocations: GPSLocationExtension[] | null = null
  showGeolocationPermissionModal = false
  showGeolocationNotSupportedModal = false
  isMapLoading = true

  // Internal state
  sessionStart = false
  sessionEnd = false
  manualInput: GameEvent | null = null
  PlaybackStatus = PlaybackStatus
  playStatus = PlaybackStatus.INIT

  isFullScreen = false
  noSleep: NoSleep | null = null

  handleClick() {
    // this variable just need to change, it doesn't matter the value
    // it's just to trigger the watcher
    // present in WebPlayerControls.vue component where the play logic is handled
    this.playFromVideoFrame = !this.playFromVideoFrame
  }

  // Init player

  initialize() {
    if (!this.platformSettings) return

    const userId = (() => {
      const urlParams = new URLSearchParams(window.location.search)
      const saveUser = urlParams.get('saveUser') === 'true'
      let userId = null

      if (saveUser) {
        const cookies = document.cookie.split(';')

        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=')

          if (name === 'userId') {
            userId = value
            break
          }
        }
      }

      if (!userId) {
        userId = `APP_USER|web-${uuidv4()}`

        if (saveUser) {
          const expirationDate = new Date()
          expirationDate.setTime(
            expirationDate.getTime() + 30 * 24 * 60 * 60 * 1000
          )
          document.cookie = `userId=${userId}; expires=${expirationDate.toUTCString()}; path=/`
        }
      }

      return userId
    })()
    this.engineService = new EngineService(
      `${this.$config.ENGINE_URL}`,
      userId,
      this.projectId,
      this.versionId,
      this.webPlayerControls.initCallbacks
    )
    this.initHandlers()

    this.initScreenWakeLock()

    const projectName = this.platformSettings.title ?? 'Not found'

    this.setExtraAnalyticsData({
      projectId: this.projectId,
      projectName,
      userId,
    })

    this.fireAnalyticsEvent('legacy_player:project_loaded')
  }

  initHandlers() {
    this.engineService!.onAudio.register(async (audioUrl: string) => {
      this.audioUrl = audioUrl
      if (!this.audioUrl) this.playStatus = PlaybackStatus.END
      await this.$nextTick()
      this.webPlayerControls.playAudio()
    })

    this.engineService!.onDisplay.register((display: Display) => {
      this.display = display
    })

    this.engineService?.onYoutube.register((youtube: GameEvent) => {
      this.display = null
      this.webPlayerControls.pause()
      this.youtube = youtube
    })

    this.engineService?.onVideo.register((video: GameEvent) => {
      this.display = null
      this.webPlayerControls.pause()
      this.video = video
    })

    this.engineService!.onButtons.register(
      (utteranceButtons: UtteranceButton[]) => {
        this.utteranceButtons = utteranceButtons
      }
    )

    this.engineService!.onTextInput.register((event: GameEvent) => {
      this.manualInput = event
    })

    this.engineService?.onGpsLocation.register(async (event: GameEvent) => {
      await waitUntil(
        () => {
          const hasAudio =
            (this.webPlayerControls?.audioPlayer?.src!! ?? false) &&
            this.webPlayerControls?.audioPlayer?.src?.length > 0
          const audioEnded = this.webPlayerControls?.audioPlayer?.ended ?? true
          return !hasAudio || audioEnded
        },
        { timeout: Infinity }
      )

      this.display = null

      if (this.geolocationService || this.aislocationService) {
        return
      }

      this.gpsLocations = (event.params.locations as GPSLocation[]).map(
        (gpsLocation) => GPSLocationExtension.fromGPSLocation(gpsLocation)
      )

      const positionHandler = (position: Position) => {
        if (this.gpsLocations === null) return
        if (this.firstLocation === null) this.firstLocation = position

        for (let index = 0; index < this.gpsLocations.length; index++) {
          const targetObject = this.gpsLocations[index]

          if (
            !targetObject.checkLocation(
              position,
              this.firstLocation ?? position
            )
          )
            continue
          if (
            this.sameAsLastTriggeredPosition(
              this.gpsLocationToHash(targetObject)
            )
          )
            continue

          this.sendPositionInput(index)

          return
        }
      }

      if (this.mmsi) {
        this.aislocationService = new AislocationService(
          this.mmsi,
          this.$config.AIS_URL
        )
        this.aislocationService.registerPositionListener(positionHandler)

        this.aislocationService.startSocket()
      } else {
        this.geolocationService = new GeolocationService()

        this.geolocationService.registerStatusListener((status) => {
          if (!this.geolocationService) return
          this.geolocationStatusHandler(status)
        })

        this.geolocationService.registerPositionListener(positionHandler)

        this.geolocationService.init()
      }
    })

    this.engineService!.onSessionEnd.register((end: boolean) => {
      this.sessionEnd = end
      this.fireAnalyticsEvent('legacy_player:project_ended')
    })

    this.engineService?.onResponse.register((response: EngineResponse) => {
      for (const evnt of response.analyticsEvents) {
        this.fireAnalyticsEvent('engine:analytics_node', {
          ...evnt,
        })
      }
    })
  }

  initScreenWakeLock() {
    const enableNoSleepOnInteraction = () => {
      this.noSleep = new NoSleep()
      try {
        this.noSleep.enable()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('NoSleep.js failed to enable:', e)
      }

      document.removeEventListener('touchstart', enableNoSleepOnInteraction)
      document.removeEventListener('mousedown', enableNoSleepOnInteraction)
      document.removeEventListener('keydown', enableNoSleepOnInteraction)
    }

    document.addEventListener('touchstart', enableNoSleepOnInteraction, false)
    document.addEventListener('mousedown', enableNoSleepOnInteraction, false)
    document.addEventListener('keydown', enableNoSleepOnInteraction, false)
  }

  beforeDestroy() {
    this.geolocationService?.destroy()
    this.geolocationService = null

    this.aislocationService?.destroy()
    this.aislocationService = null

    if (this.noSleep) {
      this.noSleep.disable()
    }
  }

  // GeoLocation stuff

  geolocationStatusHandler(status: GeolocationServiceStatus) {
    switch (status) {
      case GeolocationServiceStatus.AWAITING_PERMISSION:
        this.geolocationService?.requestPermission()
        break
      case GeolocationServiceStatus.PERMISSION_DENIED:
        this.fireAnalyticsEvent('legacy_player:geolocation_denied')
        this.showGeolocationPermissionModal = true
        break
      case GeolocationServiceStatus.NOT_SUPPORTED:
        this.showGeolocationNotSupportedModal = true
        break
      case GeolocationServiceStatus.INITIALIZING:
        break
      case GeolocationServiceStatus.READY_TO_USE:
        this.fireAnalyticsEvent('legacy_player:geolocation_authorized')
        this.geolocationService?.startWatching()
        break
      case GeolocationServiceStatus.WATCHING:
        this.isMapLoading = false
        break
    }
  }

  // AIS location stuff
  aislocationStatusHandler(status: AislocationServiceStatus) {
    switch (status) {
      case AislocationServiceStatus.READY_TO_USE:
        this.aislocationService?.startSocket()
        break
      case AislocationServiceStatus.SOCKET_OPEN:
        this.isMapLoading = false
        break
      case AislocationServiceStatus.SOCKET_CLOSED:
        this.isMapLoading = false
        break
    }
  }

  // Video stuff

  onVideoEnded() {
    if (
      (this.video && !this.video?.params?.autoplayNext) ||
      (this.youtube && !this.youtube?.params?.autoplayNext)
    ) {
      this.videoEnded = true
    } else {
      this.playAfterVideoEnd()
    }
  }

  playAfterVideoEnd() {
    this.youtube = null
    this.video = null
    this.videoEnded = false
    const vars = { system_video_ended: true }
    this.engineService?.sendInput(undefined, '', this.audioQuality, vars)
  }

  // Full screen management

  get isFullScreenSupported(): boolean {
    return this.$fullscreen.isEnabled
  }

  toggleFullScreen() {
    const container = document.getElementById('web-player-frame') ?? undefined
    this.$fullscreen.toggle(container, {
      callback: (fullscreen: boolean) => (this.isFullScreen = fullscreen),
    })
  }

  // ** Interactions
  sendRepeat() {
    this.sendInputRaw('Repeat')
    this.fireAnalyticsEvent('legacy_player:project_repeat')
  }

  sendStop() {
    this.fireAnalyticsEvent('legacy_player:project_stop')
    this.sendInputRaw('Stop')
  }

  sendRestart() {
    this.fireAnalyticsEvent('legacy_player:project_restart')
    this.sendInputRaw('Restart')
  }

  async startGame() {
    this.playStatus = PlaybackStatus.LOAD
    try {
      // Read URL params and create variables
      const vars: Record<string, any> = {}
      for (const [key, value] of Object.entries(this.$route.query)) {
        if (key !== 'embed') vars[key] = `"${value}"`
      }

      this.fireAnalyticsEvent('legacy_player:project_started')

      await this.engineService?.sendInput(
        undefined,
        'start',
        this.audioQuality,
        vars
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e)
    }
  }

  async sendButtonInteraction(button: UtteranceButton) {
    this.fireAnalyticsEvent('legacy_player:button_interaction', { button })
    this.playStatus = PlaybackStatus.LOAD
    try {
      await this.engineService?.sendInput(
        button.sendIntent,
        button.sendInput,
        this.audioQuality
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e)
    }
  }

  async sendInputRaw(text: string) {
    this.playStatus = PlaybackStatus.LOAD
    try {
      await this.engineService?.sendInput(undefined, text, this.audioQuality)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e)
    }
  }

  async sendPositionInput(index: number) {
    if (this.aislocationService) {
      this.aislocationService?.destroy()
      this.aislocationService = null
    } else {
      this.geolocationService?.destroy()
      this.geolocationService = null
    }

    this.gpsLocations = null

    this.playStatus = PlaybackStatus.LOAD
    try {
      await this.engineService?.sendInput(undefined, '', this.audioQuality, {
        system_gpsLocation: index,
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e)
    }
  }

  async sendTextInputInteraction() {
    if (!this.manualUserInput) return

    this.fireAnalyticsEvent('legacy_player:text_interaction', {
      input: this.manualUserInput,
    })
    this.playStatus = PlaybackStatus.LOAD
    try {
      await this.engineService?.sendInput(
        undefined,
        this.manualUserInput,
        this.audioQuality
      )
      this.manualUserInput = ''
      this.manualInput = null
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  sameAsLastTriggeredPosition(positionHash: string) {
    if (this.lastTriggeredPositionHash !== positionHash) {
      console.log('lastTriggeredPositionHash', this.lastTriggeredPositionHash)
      this.lastTriggeredPositionHash = positionHash
      console.log('positionHash', positionHash)
      return false
    }
    return true
  }

  gpsLocationToHash(location: GPSLocationExtension) {
    const hash = createHash('sha256')
    return hash.update(`${location.lat}${location.long}`).digest('hex')
  }

  // ** UI Styles

  get webPlayerCover() {
    const isIdle = this.playStatus === PlaybackStatus.INIT
    if (!isIdle) return {}

    const defaultCover = require('~/assets/project_thumbnail.png')
    const coverUrl = this.platformSettings.cover

    const isCoverExist = !!coverUrl && coverUrl.length > 0
    const image = isCoverExist ? coverUrl : defaultCover
    return {
      'background-image': "url('" + image + "')",
      'background-repeat': 'no-repeat',
      'background-position': 'center',
      'background-size': 'contain',
    }
  }

  get layoutClass() {
    const layout = WebPlayerThemeUtil.layoutClass(this.platformSettings)
    const justify =
      this.playStatus === PlaybackStatus.INIT
        ? 'justify-content-center'
        : 'justify-content-end'
    return [layout, justify]
  }

  get inputContainerClass() {
    const justify =
      !!this.display || !!this.video
        ? 'justify-content-center'
        : 'justify-content-end'
    return [this.layoutWidth, justify]
  }

  get layoutWidth(): string {
    if (!this.display) return 'w-100'
    if (!this.video) return 'w-100'

    // Check skinny device
    if (document.documentElement.clientWidth >= 720) {
      return 'w-100'
    }

    switch (this.platformSettings.layoutConfiguration.layout) {
      case Layout.Column:
        return 'w-100'
      case Layout.ColumnReverse:
        return 'w-100'
      case Layout.Row:
        return 'w-50'
      case Layout.RowReverse:
        return 'w-50'
    }

    return ''
  }

  get backgroundStyle() {
    return WebPlayerThemeUtil.backgroundStyle(this.platformSettings)
  }

  get buttonContainerStyle() {
    return WebPlayerThemeUtil.buttonContainerStyle(this.platformSettings)
  }

  get buttonStyle() {
    return WebPlayerThemeUtil.buttonStyle(this.platformSettings)
  }

  get defaultUtteranceButtonStyle() {
    return WebPlayerThemeUtil.defaultUtteranceButtonStyle(this.platformSettings)
  }

  get getStartButtonStyle() {
    return WebPlayerThemeUtil.getStartButtonStyle(this.platformSettings)
  }

  get micButtonStyle() {
    return WebPlayerThemeUtil.micButtonStyle(this.platformSettings)
  }

  get isPositioningEnabled() {
    return this.geolocationService !== null || this.aislocationService !== null
  }
}
